import { Component, DestroyRef, OnInit } from "@angular/core";
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { Store } from "@ngrx/store";
import {
  AdvancedSettings,
  CountryOption,
} from "../settings/settings.interface";
import {
  ChangeAdvancedSettingsAction,
  CHANGE_ADVANCED_SETTINGS_FAILURE,
  CHANGE_ADVANCED_SETTINGS_SUCCESS,
  GetAdvancedSettingsAction,
} from "../settings/settings.actions";
import { all } from "country-codes-list";
import { getCurrentAccount } from "../accounts/accounts.reducer";
import { SettingsService } from "../settings/settings.service";
import { DigitalCardService } from "../digital-cards/digital-card.service";
import type { DigitalCardProfileItem } from "../digital-cards/digital-cards.interface";
import { AuthService } from "../auth/auth.service";
import { Actions, ofType } from "@ngrx/effects";
import { forkJoin, of, take } from "rxjs";
import { catchError } from "rxjs/operators";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { AdvancedSettingsService } from "./advanced-settings.service";

@Component({
  selector: "app-advanced-settings",
  templateUrl: "./advanced-settings.component.html",
  styleUrls: ["./advanced-settings.component.scss"],
  standalone: false,
})
export class AdvancedSettingsComponent implements OnInit {
  countries: CountryOption[] = all()
    .map((country) => ({
      ...country,
      countryCallingCodeNumber: Number(country.countryCallingCode),
    }))
    .sort((a, b) => a.countryNameLocal.localeCompare(b.countryNameLocal));

  readonly identityLabel = (value: string) => value;
  readonly digitalCardTemplateLabel = (template: DigitalCardProfileItem) =>
    template.displayName || "";
  readonly digitalCardTemplateValue = (template: DigitalCardProfileItem) =>
    template.profileId;
  readonly callingCodeLabel = (country: CountryOption) =>
    `${country.flag} ${country.countryNameLocal} (+${country.countryCallingCode})`;
  readonly callingCodeValue = (country: CountryOption) =>
    country.countryCallingCodeNumber;
  readonly callingCodeSearch = (country: CountryOption) =>
    `${country.countryNameLocal} ${country.countryNameEn} ${country.countryCode} ${country.countryCallingCode} ${country.countryCallingCodeNumber}`;

  currentDomain: string | null = null;
  selectedLanguage: string;
  loading = true;
  saving = false;
  hasChanges = false;
  customLabelVariableOptions: string[] = [];
  private initialFormValue: string | null = null;
  digitalCardTemplates: DigitalCardProfileItem[] = [];
  digitalCardTemplatesLoading = false;
  digitalCardTemplatesLoaded = false;

  form = new UntypedFormGroup({
    overwritingDataBySync: new UntypedFormControl(),
    overwritingDataBySyncAndRemove: new UntypedFormControl(),
    overwritingPhotoBySync: new UntypedFormControl(),
    updateSignatureWhenGoogleDataChange: new UntypedFormControl(),
    confirmationOfSignatureInstallationByUser: new UntypedFormControl(),
    bannerDisablingByUser: new UntypedFormControl(),
    defaultCountryCallingCode: new UntypedFormControl(),
    analyticLinksEnabled: new UntypedFormControl(),
    dailySignatureReinstallEnabled: new UntypedFormControl(),
    signatureReinstallTime: new UntypedFormControl(),
    mobileLabel: new UntypedFormControl(),
    phoneLabel: new UntypedFormControl(),
    emailLabel: new UntypedFormControl(),
    customLabel: new UntypedFormControl(),
    customLabelVariable: new UntypedFormControl(),
    labelColor: new UntypedFormControl(),
    labelTextColor: new UntypedFormControl(),
    language: new UntypedFormControl(),
    avatarEnabled: new UntypedFormControl(),
    linksEnabled: new UntypedFormControl(),
    handwrittenEnabled: new UntypedFormControl(),
    badgesEnabled: new UntypedFormControl(),
    firstInstallationEmailEnabled: new UntypedFormControl(),
    digitalCardProfileId: new UntypedFormControl(null),
  });

  constructor(
    private store: Store,
    private actions$: Actions,
    private destroyRef: DestroyRef,
    private settingsService: SettingsService,
    private advancedSettingsService: AdvancedSettingsService,
    private digitalCardService: DigitalCardService,
    private auth: AuthService,
  ) {}

  get digitalCardEnabled(): boolean {
    return !!this.auth.appUser?.optionalInterfaces?.digitalCard;
  }

  ngOnInit(): void {
    this.store
      .select(getCurrentAccount)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((domain) => {
        this.currentDomain = domain;
      });

    this.loading = true;
    this.store.dispatch(new GetAdvancedSettingsAction());
    forkJoin({
      settings: this.advancedSettingsService.get(),
      labelOptions: this.settingsService
        .getCustomLabelVariableOptions()
        .pipe(catchError(() => of([] as string[]))),
      templates: this.digitalCardEnabled
        ? this.digitalCardService
            .getProfiles({ templatesOnly: true })
            .pipe(
              catchError(() =>
                of({ profiles: [] as DigitalCardProfileItem[] }),
              ),
            )
        : of({ profiles: [] as DigitalCardProfileItem[] }),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ settings, labelOptions, templates }) => {
          this.fillForm(settings);
          this.initialFormValue = JSON.stringify(this.form.value);
          this.hasChanges = false;
          this.customLabelVariableOptions = labelOptions;
          this.digitalCardTemplates = templates.profiles ?? [];
          this.digitalCardTemplatesLoaded = this.digitalCardEnabled;
          this.digitalCardTemplatesLoading = false;
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        },
      });

    this.form
      .get("dailySignatureReinstallEnabled")
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((enabled) => {
        const signatureReinstallTimeControl = this.form.get(
          "signatureReinstallTime",
        );
        if (enabled) {
          signatureReinstallTimeControl?.setValidators([
            (c) => Validators.required(c),
          ]);
          signatureReinstallTimeControl?.setValue(this.getCurrentTime());
        }
        signatureReinstallTimeControl?.updateValueAndValidity();
      });

    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.checkForChanges();
      });
  }

  loadDigitalCardTemplates(): void {
    if (
      !this.digitalCardEnabled ||
      this.digitalCardTemplatesLoaded ||
      this.digitalCardTemplatesLoading
    ) {
      return;
    }
    this.digitalCardTemplatesLoading = true;
    this.digitalCardService.getProfiles({ templatesOnly: true }).subscribe({
      next: (res) => {
        this.digitalCardTemplates = res.profiles ?? [];
        this.digitalCardTemplatesLoaded = true;
        this.digitalCardTemplatesLoading = false;
      },
      error: () => {
        this.digitalCardTemplatesLoading = false;
      },
    });
  }

  fillForm = (advancedSettings: AdvancedSettings) => {
    this.form.patchValue(
      {
        overwritingDataBySync: advancedSettings.overwritingDataBySync || false,
        overwritingDataBySyncAndRemove:
          advancedSettings.overwritingDataBySyncAndRemove || false,
        overwritingPhotoBySync:
          advancedSettings.overwritingPhotoBySync || false,
        updateSignatureWhenGoogleDataChange:
          advancedSettings.updateSignatureWhenGoogleDataChange || false,
        confirmationOfSignatureInstallationByUser:
          advancedSettings.confirmationOfSignatureInstallationByUser || false,
        bannerDisablingByUser: advancedSettings.bannerDisablingByUser || false,
        defaultCountryCallingCode: advancedSettings.defaultCountryCallingCode,
        analyticLinksEnabled: advancedSettings.analyticLinksEnabled || false,
        dailySignatureReinstallEnabled:
          advancedSettings.dailySignatureReinstallEnabled || false,
        signatureReinstallTime: advancedSettings.signatureReinstallTime || null,
        mobileLabel: advancedSettings.mobileLabel || null,
        phoneLabel: advancedSettings.phoneLabel || null,
        emailLabel: advancedSettings.emailLabel || null,
        customLabel: advancedSettings.customLabel || null,
        customLabelVariable: advancedSettings.customLabelVariable || null,
        labelColor: advancedSettings.labelColor || null,
        labelTextColor: advancedSettings.labelTextColor || null,
        language: advancedSettings.language || "",
        avatarEnabled: advancedSettings.avatarEnabled || false,
        linksEnabled: advancedSettings.linksEnabled || false,
        handwrittenEnabled: advancedSettings.handwrittenEnabled || false,
        badgesEnabled: advancedSettings.badgesEnabled || false,
        firstInstallationEmailEnabled:
          advancedSettings.firstInstallationEmailEnabled !== false,
        digitalCardProfileId: advancedSettings.digitalCardProfileId || null,
      },
      { emitEvent: false },
    );
  };

  getCurrentTime(): string {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
  }

  onSubmit = () => {
    if (this.loading) {
      return;
    }
    if (this.form.valid) {
      const formValue = this.form.value as AdvancedSettings;
      this.saving = true;
      this.store.dispatch(
        new ChangeAdvancedSettingsAction({
          ...formValue,
          signatureReinstallTime: formValue.signatureReinstallTime || null,
          mobileLabel: formValue.mobileLabel || null,
          phoneLabel: formValue.phoneLabel || null,
          emailLabel: formValue.emailLabel || null,
          customLabel: formValue.customLabel || null,
          customLabelVariable: formValue.customLabelVariable || null,
          labelColor: formValue.labelColor || null,
          labelTextColor: formValue.labelTextColor || null,
          digitalCardProfileId: formValue.digitalCardProfileId || null,
        }),
      );
      this.awaitAdvancedSettingsSave();
    } else {
      this.form.markAllAsTouched();
    }
  };

  private awaitAdvancedSettingsSave(): void {
    this.actions$
      .pipe(
        ofType(
          CHANGE_ADVANCED_SETTINGS_SUCCESS,
          CHANGE_ADVANCED_SETTINGS_FAILURE,
        ),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((action: { type: string }) => {
        this.saving = false;
        if (action.type === CHANGE_ADVANCED_SETTINGS_FAILURE) {
          return;
        }
        this.hasChanges = false;
        this.initialFormValue = JSON.stringify(this.form.value);
      });
  }

  private checkForChanges(): void {
    if (this.initialFormValue) {
      this.hasChanges =
        this.initialFormValue !== JSON.stringify(this.form.value);
    }
  }

  private normalizeHex(v: string): string {
    const s = v.replace(/^#/, "").trim();
    if (/^[0-9a-fA-F]{6}$/.test(s)) return "#" + s;
    if (/^[0-9a-fA-F]{3}$/.test(s))
      return "#" + s[0] + s[0] + s[1] + s[1] + s[2] + s[2];
    return v.startsWith("#") ? v : "#" + v;
  }

  private isValidHex(v: string): boolean {
    const s = v.startsWith("#") ? v.slice(1) : v;
    return /^[0-9a-fA-F]{3}$/.test(s) || /^[0-9a-fA-F]{6}$/.test(s);
  }

  onLabelColorChange(event: Event): void {
    const v = (event.target as HTMLInputElement).value;
    this.form.patchValue({ labelColor: this.normalizeHex(v) });
  }

  onLabelColorHexBlur(event: Event): void {
    const v = (event.target as HTMLInputElement).value?.trim() || "";
    if (this.isValidHex(v)) {
      this.form.patchValue({ labelColor: this.normalizeHex(v) });
    }
  }

  onLabelTextColorChange(event: Event): void {
    const v = (event.target as HTMLInputElement).value;
    this.form.patchValue({ labelTextColor: this.normalizeHex(v) });
  }

  onLabelTextColorHexBlur(event: Event): void {
    const v = (event.target as HTMLInputElement).value?.trim() || "";
    if (this.isValidHex(v)) {
      this.form.patchValue({ labelTextColor: this.normalizeHex(v) });
    }
  }
}
